// SPDX-FileCopyrightText: 2025 Slant Tech LLC
// SPDX-License-Identifier: GPL-3.0-or-later

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api, { ApiError, forceReauthenticate } from './api';
import KiCanvasViewer from './components/KiCanvasViewer';
import CommentPanel from './components/CommentPanel';
import SheetSelector from './components/SheetSelector';
import Header from './components/Header';
import LoadingScreen from './components/LoadingScreen';
import ErrorBanner from './components/ErrorBanner';
import ErrorPage from './components/ErrorPage';
import LoginScreen from './components/LoginScreen';
import Dashboard from './components/Dashboard';
import DiffControls, { DiffBranchInfo } from './components/DiffControls';

/**
 * Extract project_id and mr_iid from URL path
 * Supports formats:
 *   /review/{project_id}/{mr_iid}
 *   /{project_id}/{mr_iid}
 *   ?project_id=X&mr_iid=Y (query params fallback)
 */
function getProjectAndMRFromURL() {
  const path = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);

  // Try path-based: /review/{project_id}/{mr_iid} or /{project_id}/{mr_iid}
  const reviewMatch = path.match(/\/review\/(\d+)\/(\d+)/);
  if (reviewMatch) {
    return { projectId: parseInt(reviewMatch[1]), mrIid: parseInt(reviewMatch[2]) };
  }

  const directMatch = path.match(/^\/(\d+)\/(\d+)/);
  if (directMatch) {
    return { projectId: parseInt(directMatch[1]), mrIid: parseInt(directMatch[2]) };
  }

  // Fallback to query params
  const projectId = searchParams.get('project_id');
  const mrIid = searchParams.get('mr_iid');
  if (projectId && mrIid) {
    return { projectId: parseInt(projectId), mrIid: parseInt(mrIid) };
  }

  return { projectId: null, mrIid: null };
}

/**
 * Get initial theme from localStorage or system preference
 */
function getInitialTheme() {
  const saved = localStorage.getItem('kicad-review-theme');
  if (saved) return saved;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Review App - the main viewer for a specific MR
 */
function ReviewApp({ projectId, mrIid, currentUser, onLogout, theme, onToggleTheme }) {
  // Ref to KiCanvasViewer for screenshot capture
  const viewerRef = useRef(null);

  // App state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [criticalError, setCriticalError] = useState(null); // For full-page errors (403, 404, 500)
  const [config, setConfig] = useState(null);
  const [mergeRequest, setMergeRequest] = useState(null);
  const [kicadFiles, setKicadFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [comments, setComments] = useState([]);
  const [allComments, setAllComments] = useState([]); // All comments for the MR
  const [selectedComment, setSelectedComment] = useState(null);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [newCommentPosition, setNewCommentPosition] = useState(null);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);

  // Diff view state
  const [diffEnabled, setDiffEnabled] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState(null);

  // Load initial data
  useEffect(() => {
    async function loadInitialData() {
      try {
        setLoading(true);
        setError(null);
        setCriticalError(null);

        // Get config
        const configData = await api.getConfig();
        setConfig(configData);

        // Get merge request info
        const mrData = await api.getMergeRequest(mrIid, projectId);
        setMergeRequest(mrData);

        // Get KiCad files
        const filesData = await api.getKicadFiles(mrIid, projectId);
        const files = filesData.files || filesData;
        setKicadFiles(Array.isArray(files) ? files : []);

        // Select first file by default (prefer root schematic)
        if (files.length > 0) {
          const rootFile = files.find(f => f.is_root) || files[0];
          setSelectedFile(rootFile);
        }

        // Sync comments from GitLab and get all comments
        await api.syncFromGitlab(mrIid, projectId);
        const allCommentsData = await api.getComments(null, mrIid, projectId);
        setAllComments(Array.isArray(allCommentsData) ? allCommentsData : []);

      } catch (err) {
        console.error('Failed to load initial data:', err);

        // Handle different error types
        if (err instanceof ApiError) {
          // Check if this is a GitLab token error that requires re-authentication
          if (err.data?.detail === 'gitlab_token_invalid' || err.data?.action === 'reauthenticate') {
            // The API client should have already triggered redirect, but handle it here too
            console.warn('GitLab token invalid, forcing re-authentication');
            if (!err.data?.redirecting) {
              forceReauthenticate();
            }
            return;
          }

          if (err.status === 401) {
            // Generic auth error - will be handled by parent through re-render
            // Clear auth state to trigger login screen
            throw err;
          }
          if (err.status === 403) {
            setCriticalError({
              status: 403,
              title: 'Access Denied',
              message: "You don't have permission to view this merge request. Please check that you have access to the project in GitLab.",
              details: err.message
            });
            return;
          }
          if (err.status === 404) {
            setCriticalError({
              status: 404,
              title: 'Not Found',
              message: "This merge request or project doesn't exist, or you may not have access to view it.",
              details: `Project ID: ${projectId}, MR IID: ${mrIid}`
            });
            return;
          }
          if (err.status >= 500) {
            setCriticalError({
              status: err.status,
              title: 'Server Error',
              message: 'Something went wrong while loading the merge request. Please try again later.',
              details: err.message
            });
            return;
          }
        }

        // Non-critical errors show as banner
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    }

    loadInitialData();
  }, [projectId, mrIid]);

  // Filter comments for selected file
  useEffect(() => {
    if (!selectedFile) {
      setComments([]);
      return;
    }

    const filePath = selectedFile.path || selectedFile.new_path;
    const fileComments = allComments.filter(c => c.file_path === filePath);
    setComments(fileComments);
  }, [selectedFile, allComments]);

  // Load diff data when diff is enabled or file changes
  useEffect(() => {
    if (!diffEnabled || !selectedFile) {
      setDiffData(null);
      return;
    }

    let cancelled = false;

    async function loadDiff() {
      const filePath = selectedFile.path || selectedFile.new_path;

      try {
        setDiffLoading(true);
        setDiffError(null);

        const diff = await api.getFileDiff(filePath, mrIid, projectId);

        if (!cancelled) {
          setDiffData(diff);
        }
      } catch (err) {
        console.error('Failed to load diff:', err);
        if (!cancelled) {
          setDiffError(err.message || 'Failed to load diff');
          // Don't disable diff view on error, just show error state
        }
      } finally {
        if (!cancelled) {
          setDiffLoading(false);
        }
      }
    }

    loadDiff();

    return () => {
      cancelled = true;
    };
  }, [diffEnabled, selectedFile, projectId, mrIid]);

  // Handlers
  const handleFileSelect = useCallback((file) => {
    setSelectedFile(file);
    setSelectedComment(null);
    setIsAddingComment(false);
    setNewCommentPosition(null);
    // Reset diff data when file changes (will reload if diff is enabled)
    setDiffData(null);
  }, []);

  const handleToggleDiff = useCallback(() => {
    setDiffEnabled(prev => !prev);
  }, []);

  const handleCanvasClick = useCallback((x, y, componentRef = null) => {
    if (isAddingComment) {
      setNewCommentPosition({ x, y, componentRef });
    } else {
      setSelectedComment(null);
    }
  }, [isAddingComment]);

  const handleCommentMarkerClick = useCallback((comment) => {
    setSelectedComment(comment);
    setIsAddingComment(false);
    setNewCommentPosition(null);
  }, []);

  const handleStartAddComment = useCallback(() => {
    setIsAddingComment(true);
    setSelectedComment(null);
  }, []);

  const handleCancelAddComment = useCallback(() => {
    setIsAddingComment(false);
    setNewCommentPosition(null);
  }, []);

  const handleCreateComment = useCallback(async (body, componentRef = null) => {
    if (!selectedFile || !newCommentPosition) return;

    try {
      setIsCapturingScreenshot(true);

      // Capture screenshot of the area around the comment
      let screenshotBase64 = null;
      if (viewerRef.current?.captureScreenshot) {
        try {
          screenshotBase64 = await viewerRef.current.captureScreenshot(newCommentPosition);
        } catch (screenshotErr) {
          console.warn('Screenshot capture failed, continuing without screenshot:', screenshotErr);
        }
      }

      setIsCapturingScreenshot(false);

      const filePath = selectedFile.path || selectedFile.new_path;
      const newComment = await api.createComment({
        file_path: filePath,
        file_type: selectedFile.file_type,
        position_x: newCommentPosition.x,
        position_y: newCommentPosition.y,
        content: body,
        component_ref: componentRef || newCommentPosition.componentRef,
        screenshot_base64: screenshotBase64,
      }, true, mrIid, projectId);

      setAllComments(prev => [...prev, newComment]);
      setIsAddingComment(false);
      setNewCommentPosition(null);
      setSelectedComment(newComment);
    } catch (err) {
      console.error('Failed to create comment:', err);
      setError('Failed to create comment');
      setIsCapturingScreenshot(false);
    }
  }, [selectedFile, newCommentPosition, projectId, mrIid]);

  const handleUpdateComment = useCallback(async (commentId, updateData) => {
    try {
      const updated = await api.updateComment(commentId, updateData);
      setAllComments(prev => prev.map(c => c.id === commentId ? updated : c));
      if (selectedComment?.id === commentId) {
        setSelectedComment(updated);
      }
    } catch (err) {
      console.error('Failed to update comment:', err);
      setError('Failed to update comment');
    }
  }, [selectedComment]);

  const handleDeleteComment = useCallback(async (commentId) => {
    try {
      await api.deleteComment(commentId);
      setAllComments(prev => prev.filter(c => c.id !== commentId));
      if (selectedComment?.id === commentId) {
        setSelectedComment(null);
      }
    } catch (err) {
      console.error('Failed to delete comment:', err);
      setError('Failed to delete comment');
    }
  }, [selectedComment]);

  const handleResolveComment = useCallback(async (commentId, resolved) => {
    await handleUpdateComment(commentId, { resolved });
  }, [handleUpdateComment]);

  const handleSyncFromGitlab = useCallback(async () => {
    try {
      await api.syncFromGitlab(mrIid, projectId);
      const allCommentsData = await api.getComments(null, mrIid, projectId);
      setAllComments(Array.isArray(allCommentsData) ? allCommentsData : []);
    } catch (err) {
      console.error('Failed to sync from GitLab:', err);
      setError('Failed to sync from GitLab');
    }
  }, [projectId, mrIid]);

  const dismissError = useCallback(() => {
    setError(null);
  }, []);

  const handleRetry = useCallback(() => {
    setCriticalError(null);
    setLoading(true);
    // Force re-run of effect by updating a dependency would be complex,
    // so we just reload the page for simplicity
    window.location.reload();
  }, []);

  // Render critical error page for 403, 404, 500 errors
  if (criticalError) {
    return (
      <ErrorPage
        statusCode={criticalError.status}
        title={criticalError.title}
        message={criticalError.message}
        details={criticalError.details}
        showLogin={criticalError.status === 401}
        showHome={true}
        showRetry={criticalError.status >= 500}
        onRetry={handleRetry}
      />
    );
  }

  // Render loading screen while loading data
  if (loading) {
    return <LoadingScreen />;
  }

  // Render main app
  return (
    <div className="h-screen flex flex-col bg-st-primary overflow-hidden">
      {error && <ErrorBanner message={error} onDismiss={dismissError} />}

      <Header
        mergeRequest={mergeRequest}
        config={config}
        currentUser={currentUser}
        onSync={handleSyncFromGitlab}
        onLogout={onLogout}
        theme={theme}
        onToggleTheme={onToggleTheme}
      />

      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Left Sidebar - Sheet Selector */}
        <div className="w-64 flex-shrink-0 border-r-2 border-st-border bg-st-primary overflow-hidden flex flex-col">
          <SheetSelector
            files={kicadFiles}
            selectedFile={selectedFile}
            onSelectFile={handleFileSelect}
            comments={allComments}
            projectId={projectId}
            mrIid={mrIid}
          />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Diff Controls Toolbar */}
          {selectedFile && (
            <div className="flex-shrink-0 bg-st-primary border-b-2 border-st-border px-4 py-2 flex items-center justify-between">
              <DiffControls
                diffEnabled={diffEnabled}
                onToggle={handleToggleDiff}
                diffData={diffData}
                isLoading={diffLoading}
                error={diffError}
              />

              {/* Branch info when diff is enabled */}
              {diffEnabled && diffData && (
                <DiffBranchInfo diffData={diffData} />
              )}
            </div>
          )}

          {/* KiCanvas Viewer */}
          <div className="flex-1 relative bg-st-secondary min-h-0">
            {selectedFile ? (
              <KiCanvasViewer
                ref={viewerRef}
                file={selectedFile}
                comments={comments}
                selectedComment={selectedComment}
                isAddingComment={isAddingComment}
                newCommentPosition={newCommentPosition}
                onCanvasClick={handleCanvasClick}
                onCommentClick={handleCommentMarkerClick}
                projectId={projectId}
                mrIid={mrIid}
                diffEnabled={diffEnabled}
                diffData={diffData}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="empty-state">
                  <div className="empty-state-icon"><i className="bx bx-folder-open"></i></div>
                  <h3 className="text-lg font-display font-medium mb-2">No KiCad Files Found</h3>
                  <p className="text-sm font-body">This merge request doesn't contain any KiCad schematic or PCB files.</p>
                </div>
              </div>
            )}

            {/* Screenshot capture overlay */}
            {isCapturingScreenshot && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-50">
                <div className="bg-st-primary px-6 py-4 shadow-lg flex items-center gap-3">
                  <div className="spinner w-5 h-5" />
                  <span className="font-body">Capturing screenshot...</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Comment Panel */}
        <CommentPanel
          comments={comments}
          selectedComment={selectedComment}
          isAddingComment={isAddingComment}
          newCommentPosition={newCommentPosition}
          gitlabUrl={config?.gitlab_url || ''}
          projectId={projectId}
          onStartAdd={handleStartAddComment}
          onCancelAdd={handleCancelAddComment}
          onCreateComment={handleCreateComment}
          onSelectComment={handleCommentMarkerClick}
          onUpdateComment={handleUpdateComment}
          onDeleteComment={handleDeleteComment}
          onResolveComment={handleResolveComment}
        />
      </div>
    </div>
  );
}

/**
 * Main App component - handles routing and auth
 */
function App() {
  // Get project/MR from URL
  const { projectId, mrIid } = useMemo(() => getProjectAndMRFromURL(), []);

  // Theme state
  const [theme, setTheme] = useState(getInitialTheme);

  const handleToggleTheme = useCallback(() => {
    setTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('kicad-review-theme', next);
      if (next === 'dark') {
        document.documentElement.classList.add('dark-theme');
      } else {
        document.documentElement.classList.remove('dark-theme');
      }
      return next;
    });
  }, []);

  // Auth state
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authEnabled, setAuthEnabled] = useState(true);

  // Check authentication status first
  useEffect(() => {
    async function checkAuth() {
      try {
        const authStatus = await api.getAuthStatus();
        setAuthEnabled(authStatus.auth_enabled !== false);

        if (authStatus.authenticated) {
          setIsAuthenticated(true);
          setCurrentUser(authStatus.user);
        } else if (!authStatus.auth_enabled) {
          // Auth disabled, allow access
          setIsAuthenticated(true);
          setCurrentUser({ username: 'anonymous', name: 'Anonymous' });
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        // If auth check fails with 401, we need to login
        if (err.status === 401) {
          setIsAuthenticated(false);
        }
      } finally {
        setAuthChecked(true);
      }
    }

    checkAuth();
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await api.logout();
      setIsAuthenticated(false);
      setCurrentUser(null);
      // Redirect to home
      window.location.href = '/';
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, []);

  // Waiting for auth check
  if (!authChecked) {
    return <LoadingScreen />;
  }

  // Not authenticated - show login screen
  if (!isAuthenticated) {
    return <LoginScreen authEnabled={authEnabled} />;
  }

  // No project/MR specified - show dashboard
  if (!projectId || !mrIid) {
    return (
      <Dashboard
        currentUser={currentUser}
        onLogout={handleLogout}
        theme={theme}
        onToggleTheme={handleToggleTheme}
      />
    );
  }

  // Show review app for specific MR
  return (
    <ReviewApp
      projectId={projectId}
      mrIid={mrIid}
      currentUser={currentUser}
      onLogout={handleLogout}
      theme={theme}
      onToggleTheme={handleToggleTheme}
    />
  );
}

export default App;
